import { useTranslation } from 'react-i18next';

import Info from '@/assets/icons/info.svg?component';
import TitleIcon from '@/components/atoms/TitleIcon/TitleIcon';
import { ChartHeaderActions } from '@/components/charts/actions';
import type { MetricChartSpec } from '@/components/charts/generic';
import PageLayout from '@/components/Layout/PageLayout';
import ContentBlock from '@/components/molecules/ContentBlock';
import type { DateRange } from '@/components/molecules/DateRangePicker/DateRangePicker';
import type { DateRangePresetId } from '@/components/molecules/DateRangePicker/DateRangePresets';
import EmptyState from '@/components/molecules/EmptyState';
import type { RetryProps } from '@/components/retryFailedQueries';
import SomethingHappened from '@/components/SomethingHappened';
import { useChartActions } from '@/hooks/useChartActions';
import GridLayout from '@/routes/Client/Cage/pages/Reports/shared/GridLayout';

import { CageLegend, type CageLegendItem } from './components/CageLegend';
import { CompareLineChart } from './components/CompareLineChart';
import { MetricMenu, type MetricMenuGroup } from './components/MetricMenu';
import { areAllMetricOptionsDisabled } from './helpers/availableMetrics';

import styles from './LocationCompare.module.scss';

export interface LocationCompareViewProps {
    locationName: string;
    isLoading: boolean;
    error: Error | null;
    /** Try again in the error state. */
    retry?: RetryProps;
    dateFrom?: string;
    dateTo?: string;
    onDateChange: (range: DateRange) => void;
    metricGroups: MetricMenuGroup[];
    selectedMetricId?: string;
    onMetricChange: (next: string) => void;
    chartSpec: MetricChartSpec<'line'>;
    legendItems: CageLegendItem[];
    visibleCageIds: Set<number>;
    onToggleCage: (cageId: number) => void;
    /** The depth paragraph, shown while hourly depth is switched on. */
    showDepthInfo?: boolean;
    /** The hourly request was shortened to 31 days. */
    hourlyRangeClamped?: boolean;
    /** Hourly depth uses the short menu: last 24 hours through 14 days, defaulting to 7 days. */
    hourlySelected?: boolean;
}

const HOURLY_COMPARE_HIDDEN_PRESETS: readonly DateRangePresetId[] = [
    'last4weeks',
    'last3months',
    'all',
];

const CHART_HEIGHT = 490;
const INFO_COLUMN_WIDTH = 375;

export default function LocationCompareView(props: LocationCompareViewProps) {
    const {
        locationName,
        isLoading,
        error,
        retry,
        dateFrom,
        dateTo,
        onDateChange,
        metricGroups,
        selectedMetricId,
        onMetricChange,
        chartSpec,
        legendItems,
        visibleCageIds,
        onToggleCage,
        showDepthInfo = false,
        hourlyRangeClamped = false,
        hourlySelected = false,
    } = props;
    const { t } = useTranslation('locationCompare');

    const { chartRef, fullScreenContainerRef, isFullScreen, handleFullScreenToggle } =
        useChartActions();

    // Injected environmental params can fill the dropdown with only disabled
    // options. Treat that the same as no groups so we show the empty state
    // instead of a blank chart.
    const noMetrics = !isLoading && areAllMetricOptionsDisabled(metricGroups);
    const noCages = !isLoading && legendItems.length === 0;
    const hasContent = !noMetrics && !noCages;
    const selectedMetricLabel = metricGroups
        .flatMap((group) => group.options)
        .find((option) => option.value === selectedMetricId)?.label;

    return (
        <PageLayout title={locationName} subtitle={t('title')}>
            <GridLayout contentLayout="1-fixed" secondColumnWidth={INFO_COLUMN_WIDTH}>
                <ContentBlock containerRef={fullScreenContainerRef}>
                    <div className={styles.chartHeader}>
                        <MetricMenu
                            label={t('comparing')}
                            value={selectedMetricId ?? ''}
                            groups={metricGroups}
                            onChange={onMetricChange}
                            disabled={isLoading || noMetrics}
                        />
                        <ChartHeaderActions
                            chartRef={chartRef}
                            chartName="location-compare-chart"
                            isFullScreen={isFullScreen}
                            onFullScreenToggle={handleFullScreenToggle}
                            dateFrom={dateFrom}
                            dateTo={dateTo}
                            onDateChange={onDateChange}
                            defaultPresetId={hourlySelected ? 'last7days' : 'last4weeks'}
                            show24hPreset={hourlySelected}
                            showLast3DaysPreset={hourlySelected}
                            hiddenPresetIds={
                                hourlySelected ? HOURLY_COMPARE_HIDDEN_PRESETS : undefined
                            }
                            disabled={isLoading}
                            menuDisabled={!hasContent}
                        />
                    </div>

                    {error ? (
                        <SomethingHappened {...retry} />
                    ) : noMetrics ? (
                        <EmptyState
                            title={t('noMetricsAvailable')}
                            appearance="quiet"
                            height={CHART_HEIGHT}
                        />
                    ) : noCages ? (
                        <EmptyState title={t('noCages')} appearance="quiet" height={CHART_HEIGHT} />
                    ) : (
                        <CompareLineChart
                            chartRef={chartRef}
                            spec={chartSpec}
                            chartHeight={CHART_HEIGHT}
                            isLoading={isLoading}
                            title={selectedMetricLabel}
                            legend={
                                <CageLegend
                                    items={legendItems}
                                    visibleCageIds={visibleCageIds}
                                    onToggle={onToggleCage}
                                />
                            }
                        />
                    )}
                </ContentBlock>

                <ContentBlock title={t('infoLabel')} titleIcon={<TitleIcon icon={Info} />}>
                    {showDepthInfo && <div className={styles.infoText}>{t('depthInfo')}</div>}
                    {hourlyRangeClamped && (
                        <div className={styles.infoText}>{t('hourlyRangeNote')}</div>
                    )}
                    <div className={styles.infoText}>{t('infoText')}</div>
                </ContentBlock>
            </GridLayout>
        </PageLayout>
    );
}
